import { Vector3 } from 'three'
import type { Brick, BrickType, BrickColor } from '@models/brick'
import { v4 as uuidv4 } from 'uuid'

/**
 * Random Brick Generation Utilities
 *
 * Utilities for generating random bricks for performance testing and debugging.
 * Used by the debug panel to create bulk bricks for benchmarking.
 *
 * Features:
 * - Random brick types (1x1, 1x2, 2x2, 2x4, 1x4)
 * - Random LEGO colors (6 preset options)
 * - Random rotations (0°, 90°, 180°, 270°)
 * - Random positions within defined bounds
 *
 * Task: 4.1.0 - Debug mode bulk brick generation
 */

/**
 * Available brick types for random generation
 */
const BRICK_TYPES: BrickType[] = ['1x1', '1x2', '2x2', '2x4', '1x4']

/**
 * Available rotations (in degrees)
 */
const ROTATIONS = [0, 90, 180, 270]

/**
 * Classic LEGO color palette
 * 6 iconic LEGO colors for variety
 */
const LEGO_COLORS: BrickColor[] = [
  { r: 220, g: 20, b: 60 },   // Crimson Red
  { r: 0, g: 85, b: 191 },    // LEGO Blue
  { r: 0, g: 143, b: 0 },     // LEGO Green
  { r: 255, g: 205, b: 0 },   // LEGO Yellow
  { r: 0, g: 0, b: 0 },       // Black
  { r: 255, g: 255, b: 255 }, // White
]

/**
 * Bounds for random brick placement
 * Defines the area where bricks can be randomly placed
 */
const PLACEMENT_BOUNDS = {
  minX: -20,
  maxX: 20,
  minY: 0.48,  // Half brick height to place on ground
  maxY: 20,    // Stack up to 20 units high
  minZ: -20,
  maxZ: 20,
}

/**
 * Get a random element from an array
 */
function randomElement<T>(array: T[]): T {
  const element = array[Math.floor(Math.random() * array.length)]
  if (element === undefined) {
    throw new Error('Cannot get random element from empty array')
  }
  return element
}

/**
 * Get a random number between min and max (inclusive)
 */
function randomRange(min: number, max: number): number {
  return Math.random() * (max - min) + min
}

/**
 * Snap value to grid (0.8 unit spacing)
 */
function snapToGrid(value: number): number {
  const gridSize = 0.8
  return Math.round(value / gridSize) * gridSize
}

/**
 * Generate a random brick with random properties
 *
 * @returns A randomly generated brick
 */
export function generateRandomBrick(): Brick {
  const type = randomElement(BRICK_TYPES)
  const color = randomElement(LEGO_COLORS)
  const rotation = randomElement(ROTATIONS)

  // Generate random position within bounds, snapped to grid
  const x = snapToGrid(randomRange(PLACEMENT_BOUNDS.minX, PLACEMENT_BOUNDS.maxX))
  const y = snapToGrid(randomRange(PLACEMENT_BOUNDS.minY, PLACEMENT_BOUNDS.maxY))
  const z = snapToGrid(randomRange(PLACEMENT_BOUNDS.minZ, PLACEMENT_BOUNDS.maxZ))

  const position = new Vector3(x, y, z)

  return {
    id: uuidv4(),
    type,
    position,
    rotation,
    color,
    connections: [],
  }
}

/**
 * Generate multiple random bricks
 *
 * @param count - Number of bricks to generate
 * @returns Array of randomly generated bricks
 */
export function generateRandomBricks(count: number): Brick[] {
  const bricks: Brick[] = []

  for (let i = 0; i < count; i++) {
    bricks.push(generateRandomBrick())
  }

  return bricks
}

/**
 * Generate random bricks in a specific area
 *
 * @param count - Number of bricks to generate
 * @param minPos - Minimum position (x, y, z)
 * @param maxPos - Maximum position (x, y, z)
 * @returns Array of randomly generated bricks within bounds
 */
export function generateRandomBricksInArea(
  count: number,
  minPos: Vector3,
  maxPos: Vector3
): Brick[] {
  const bricks: Brick[] = []

  for (let i = 0; i < count; i++) {
    const type = randomElement(BRICK_TYPES)
    const color = randomElement(LEGO_COLORS)
    const rotation = randomElement(ROTATIONS)

    const x = snapToGrid(randomRange(minPos.x, maxPos.x))
    const y = snapToGrid(randomRange(minPos.y, maxPos.y))
    const z = snapToGrid(randomRange(minPos.z, maxPos.z))

    const position = new Vector3(x, y, z)

    bricks.push({
      id: uuidv4(),
      type,
      position,
      rotation,
      color,
      connections: [],
    })
  }

  return bricks
}

/**
 * Generate random bricks with specific type distribution
 *
 * @param count - Number of bricks to generate
 * @param typeWeights - Weight for each brick type (e.g., { '1x1': 0.5, '2x4': 0.3 })
 * @returns Array of randomly generated bricks with weighted types
 */
export function generateWeightedRandomBricks(
  count: number,
  typeWeights: Partial<Record<BrickType, number>>
): Brick[] {
  // Create weighted array of types
  const weightedTypes: BrickType[] = []
  Object.entries(typeWeights).forEach(([type, weight]) => {
    const repetitions = Math.round((weight || 0) * 100)
    for (let i = 0; i < repetitions; i++) {
      weightedTypes.push(type as BrickType)
    }
  })

  const bricks: Brick[] = []

  for (let i = 0; i < count; i++) {
    const type = randomElement(weightedTypes.length > 0 ? weightedTypes : BRICK_TYPES)
    const color = randomElement(LEGO_COLORS)
    const rotation = randomElement(ROTATIONS)

    const x = snapToGrid(randomRange(PLACEMENT_BOUNDS.minX, PLACEMENT_BOUNDS.maxX))
    const y = snapToGrid(randomRange(PLACEMENT_BOUNDS.minY, PLACEMENT_BOUNDS.maxY))
    const z = snapToGrid(randomRange(PLACEMENT_BOUNDS.minZ, PLACEMENT_BOUNDS.maxZ))

    const position = new Vector3(x, y, z)

    bricks.push({
      id: uuidv4(),
      type,
      position,
      rotation,
      color,
      connections: [],
    })
  }

  return bricks
}
